import { supabase } from './supabase'
import { computeReplenishment } from './replenishment'
import type { ReplenishmentPriority } from '../types'

/** Linha crua devolvida pela RPC get_linked_filial_replenishment. */
interface FilialReplenishmentRaw {
  filial_company_id: string
  filial_name: string
  sku: string
  product_name: string
  current_stock: number
  monthly_avg: number
  /** Lead time em dias; `null` quando a filial não tem fornecedor com lead time cadastrado. */
  lead_time: number | null
  /** Soma dos pedidos Pendente/Enviado da filial para o SKU; pode faltar em RPCs antigas. */
  open_qty?: number | null
}

/** Linha de necessidade de uma filial, já com os campos derivados. */
export interface FilialNeedRow {
  filial_company_id: string
  filial_name: string
  sku: string
  product_name: string
  current_stock: number
  gap_to_buy: number
  purchase_suggestion: number
  priority: ReplenishmentPriority
}

/**
 * Busca a necessidade de reposição de todas as filiais vinculadas à empresa
 * ativa (matriz) e deriva os campos com a MESMA fórmula do Painel BX
 * (computeReplenishment). A RPC já valida acesso e isola por company_links.
 *
 * `safetyDays` aplica o colchão anti-ruptura (Fase 1) também às filiais, mantendo
 * paridade de fórmula com a matriz. A RPC não retorna a curva ABC da filial, então
 * usa-se um valor único (o tier C resolvido na matriz); o colchão por curva nas
 * filiais depende de a RPC passar a expor a curva (Fase 2).
 */
export async function fetchFilialNeeds(safetyDays = 0): Promise<FilialNeedRow[]> {
  const { data, error } = await supabase.rpc('get_linked_filial_replenishment')
  if (error) throw error
  return ((data ?? []) as FilialReplenishmentRaw[]).map(r => {
    const calc = computeReplenishment({
      current_stock: Number(r.current_stock),
      monthly_avg: Number(r.monthly_avg),
      lead_time: r.lead_time === null || r.lead_time === undefined ? null : Number(r.lead_time),
      open_qty: Number(r.open_qty ?? 0),
      safety_days: safetyDays,
    })
    return {
      filial_company_id: r.filial_company_id,
      filial_name: r.filial_name,
      sku: r.sku,
      product_name: r.product_name,
      current_stock: Number(r.current_stock),
      gap_to_buy: calc.gap_to_buy,
      purchase_suggestion: calc.purchase_suggestion,
      priority: calc.priority,
    }
  })
}

export interface FilialSuggestion {
  total: number
  breakdown: Array<{ filial_name: string; qty: number }>
}

/** Agrupa a necessidade das filiais por SKU, somando a sugestão de compra. */
export function buildFilialSuggestionMap(rows: FilialNeedRow[]): Map<string, FilialSuggestion> {
  const map = new Map<string, FilialSuggestion>()
  for (const r of rows) {
    if (r.purchase_suggestion <= 0) continue
    const entry = map.get(r.sku) ?? { total: 0, breakdown: [] }
    entry.total += r.purchase_suggestion
    entry.breakdown.push({ filial_name: r.filial_name, qty: r.purchase_suggestion })
    map.set(r.sku, entry)
  }
  return map
}
